import { useState, useEffect } from 'react'
import { Shield, Save, X, Settings, AlertCircle, CheckCircle } from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken } from '@/hooks/useAuth'
import { API_BASE } from '@/services/api'

interface SlaPolicies {
  urgent: { first_response: number; resolution: number }
  high: { first_response: number; resolution: number }
  normal: { first_response: number; resolution: number }
  low: { first_response: number; resolution: number }
}

const DEFAULT_POLICIES: SlaPolicies = {
  urgent: { first_response: 60, resolution: 240 },
  high: { first_response: 240, resolution: 480 },
  normal: { first_response: 480, resolution: 1440 },
  low: { first_response: 1440, resolution: 4320 },
}

const PRIORITY_META: Record<string, { label: string; color: string; dot: string }> = {
  urgent: { label: 'Urgent', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  normal: { label: 'Normal', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  low: { label: 'Low', color: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
}

// Module-level cache
let _cachedSlaPolicies: SlaPolicies | null = null

const formatMinutes = (mins: number): string => {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export default function AISlaPage() {
  const { tenantId } = useTenant()
  const [policies, setPolicies] = useState<SlaPolicies>(_cachedSlaPolicies || DEFAULT_POLICIES)
  const [loading, setLoading] = useState(!_cachedSlaPolicies)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<SlaPolicies>({ ...policies })

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token && token !== 'demo-token') {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  useEffect(() => {
    if (tenantId) loadConfig()
  }, [tenantId])

  const loadConfig = async () => {
    try {
      if (!_cachedSlaPolicies) setLoading(true)
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/router/sla-config`, { headers })
      if (response.ok) {
        const data = await response.json()
        if (data.policies) {
          setPolicies(data.policies)
          setFormData(data.policies)
          _cachedSlaPolicies = data.policies
        }
      }
    } catch (err: any) {
      console.error('Failed to load SLA config:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/router/sla-config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ policies: formData }),
      })
      if (response.ok) {
        setPolicies(formData)
        _cachedSlaPolicies = formData
        setIsEditing(false)
      } else {
        setError('Failed to save SLA policies')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({ ...policies })
    setIsEditing(false)
    setError(null)
  }

  const updatePolicy = (priority: keyof SlaPolicies, field: 'first_response' | 'resolution', value: number) => {
    setFormData(prev => ({
      ...prev,
      [priority]: { ...prev[priority], [field]: value },
    }))
  }

  if (loading && !_cachedSlaPolicies) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="w-8 h-8 mr-3 text-amber-500" />
            SLA Policies
          </h1>
          <p className="text-gray-600 mt-2">
            Define response and resolution time targets for each priority level
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}

      {/* Configuration Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">SLA Configuration</h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Priority</th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">First Response Target</th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Resolution Target</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(Object.keys(PRIORITY_META) as Array<keyof SlaPolicies>).map((priority) => {
                const meta = PRIORITY_META[priority]
                const data = isEditing ? formData[priority] : policies[priority]
                return (
                  <tr key={priority} className="hover:bg-gray-50/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2.5">
                        <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border ${meta.color}`}>
                          {meta.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {isEditing ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min={1}
                            value={data.first_response}
                            onChange={(e) => updatePolicy(priority, 'first_response', parseInt(e.target.value) || 0)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
                          />
                          <span className="text-xs text-gray-500">minutes ({formatMinutes(data.first_response)})</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{formatMinutes(data.first_response)}</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {isEditing ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min={1}
                            value={data.resolution}
                            onChange={(e) => updatePolicy(priority, 'resolution', parseInt(e.target.value) || 0)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
                          />
                          <span className="text-xs text-gray-500">minutes ({formatMinutes(data.resolution)})</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{formatMinutes(data.resolution)}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-3">How SLA Policies Work</h3>
        <div className="space-y-2 text-sm text-gray-700">
          <p><strong>First Response Target:</strong> Maximum time before a support agent responds to the customer.</p>
          <p><strong>Resolution Target:</strong> Maximum time to fully resolve the ticket.</p>
          <p>When a ticket exceeds its SLA target, it's flagged as <strong>breached</strong> and appears with a red indicator in the inbox. SLA timers are visible on each ticket.</p>
        </div>
      </div>

      {/* Status */}
      {!isEditing && !error && !loading && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-600">SLA policies are active and applied to all new tickets</span>
        </div>
      )}
    </div>
  )
}
